import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomFileUpload, CustomInput } from '../Components/CustomFormElements';
import SureLoader from '../Components/SureLoader';
import { DocumentApiProvider, useDocumentApi } from '../Services/DocumentApiProvider';

const defaultFormats = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
  'text/plain', // txt
  'text/markdown', // md
  'text/csv',
  'text/html',
];

const allowedExtensions = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md', 'csv', 'html'];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function CreateDocument({ canEdit }) {
  const { organizationId } = useParams();
  const { uploadDocument, uploadDocumentFromLink } = useDocumentApi();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [link, setLink] = useState('');
  const [isValidLink, setIsValidLink] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const inputField = document.getElementById('document-link');
    if (inputField) {
      inputField.addEventListener('focus', () => {
        inputField.classList.remove('input-error');
      });
    }
  }, []);

  const onFileSelect = (selectedFiles) => {
    setFile(selectedFiles[0] || null);
  };

  const validateForm = () => {
    if (!file && !link.trim()) {
      showAlert('fail', 'Please provide either a file or a link.');
      return false;
    }

    if (link.trim() && !/^(https?:\/\/)([\w.-]+)\.([a-z]{2,6})(\/[^\s]*)?\/?$/.test(link)) {
      showAlert('fail', 'Invalid link format. Please enter a valid URL.');
      setIsValidLink(false);
      return false;
    }

    return true;
  };

  const handleCreateDocument = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      if (file) {
        await uploadDocument(file);
      } else if (link.trim()) {
        await uploadDocumentFromLink(link);
      }

      navigate(`/organizations/${organizationId}/documents-hub`, {
        state: { successMessage: 'Document uploaded successfully!' },
      });
    } catch (error) {
      console.error('Error during document upload', error);
      showAlert('fail', 'An error occurred during the upload. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    showConfirmation('Are you sure you want to cancel creating a new document?', () =>
      navigate(`/organizations/${organizationId}/documents-hub`),
    );
  };
  const handleLinkChange = (id, value) => {
    setLink(value);
    setFile(null);
    setIsValidLink(true);
  };

  const handleClear = () => {
    setFile(null);
    setLink('');
  };

  return (
    <div className="container">
      <Breadcrumbs />
      {isUploading && <SureLoader fullscreen />}
      <div className="row d-flex gap">
        <div className="d-flex radius-yes p-2 bg-yes col-6 flex-column">
          <div className="int-col-4">
            <h2 className="h2-title">Create a New Document</h2>
          </div>

          <div className="int-col-4 gap d-flex flex-row-to-col flex-column">
            <div className="int-col-2 d-flex p-2 border-yes radius-yes full-width-on-sml-scr gap flex-column">
              {/* Upload Document Section */}
              <CustomFileUpload
                multiple={false}
                maxFileSize={MAX_FILE_SIZE}
                allowedFormats={defaultFormats}
                allowedExtensions={allowedExtensions}
                showUploadedFiles={true}
                allowFileDeletion={true}
                onFileSelect={onFileSelect}
                showAlert={showAlert}
                showConfirmation={showConfirmation}
              />

              {/* OR Link Section */}
              <div className="section-separator w-100 d-flex justify-center p-2 large-font">
                Or provide a link to the site
              </div>
              <div className="input-row d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Link to site</div>
                  <div className="small-font">Enter URL</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="document-link"
                    label="Link"
                    placeholder="Enter document link..."
                    value={link}
                    onChange={handleLinkChange}
                    type="text"
                    disabled={!!file}
                    className={isValidLink ? 'input-ok' : 'input-error'}
                  />
                </div>
              </div>
            </div>
            {/* Buttons */}
            <div className="buttons-block w-100 justify-end d-flex flex-row flex-row-to-col">
              {canEdit && (
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleCreateDocument}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Create Document'}
                </Button>
              )}
              <Button variant="primary" size="medium" onClick={handleClear}>
                Clear
              </Button>
              <Button variant="danger" size="medium" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// export default CreateDocument;
function NewDocumentWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <DocumentApiProvider>
        <CreateDocument canEdit={canEdit} />
      </DocumentApiProvider>
    </ConfirmationProvider>
  );
}

export default NewDocumentWrapper;
